import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

// Tipos para clientes
export interface Cliente {
  id: string;
  email: string;
  nombre: string;
  empresa?: string;
  telefono?: string;
  direccion?: string;
  ciudad?: string;
  pais?: string;
  is_active: boolean;
  last_login?: string;
}

export interface ClienteSession {
  token: string;
  expires_at: string;
}

export interface ClienteAuthContextType {
  cliente: Cliente | null;
  session: ClienteSession | null;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
}

const ClienteAuthContext = createContext<ClienteAuthContextType | undefined>(undefined);

export const useClienteAuth = () => {
  const context = useContext(ClienteAuthContext);
  if (context === undefined) {
    throw new Error('useClienteAuth must be used within a ClienteAuthProvider');
  }
  return context;
};

interface ClienteAuthProviderProps {
  children: ReactNode;
}

export const ClienteAuthProvider: React.FC<ClienteAuthProviderProps> = ({ children }) => {
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [session, setSession] = useState<ClienteSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Verificar sesión existente al cargar
  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        const token = localStorage.getItem('cliente_token');
        if (token) {
          const { data, error } = await supabase.rpc('validate_cliente_session', {
            p_token: token
          });

          if (error) throw error;

          if (data && data.length > 0 && data[0].is_valid) {
            const sessionData = data[0];
            setCliente({
              id: sessionData.cliente_id,
              email: '', // Se llenará con datos del cliente
              nombre: sessionData.nombre,
              empresa: sessionData.empresa,
              is_active: true
            });
            setSession({ token, expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() });
          } else {
            localStorage.removeItem('cliente_token');
          }
        }
      } catch (error) {
        console.error('Error checking existing session:', error);
        localStorage.removeItem('cliente_token');
      } finally {
        setIsLoading(false);
      }
    };

    checkExistingSession();
  }, []);

  // Login de cliente
  const login = async (email: string, password: string): Promise<boolean> => {
    console.log('Iniciando login para cliente:', email);
    try {
      setIsLoading(true);
      setError(null);

      // Autenticar cliente
      const { data, error } = await supabase.rpc('authenticate_cliente', {
        p_email: email,
        p_password: password
      });

      if (error) throw error;

      if (data && data.length > 0 && data[0].success) {
        const authData = data[0];
        
        // Crear sesión
        const { data: sessionData, error: sessionError } = await supabase.rpc('create_cliente_session', {
          p_cliente_id: authData.cliente_id
        });

        if (sessionError) throw sessionError;

        if (sessionData && sessionData.length > 0) {
          const sessionInfo = sessionData[0];
          
          // Guardar token en localStorage
          localStorage.setItem('cliente_token', sessionInfo.token);
          
          // Actualizar estado
          setCliente({
            id: authData.cliente_id,
            email,
            nombre: authData.nombre,
            empresa: authData.empresa,
            is_active: true
          });
          
          setSession({
            token: sessionInfo.token,
            expires_at: sessionInfo.expires_at
          });

          // Log login activity
          console.log('Intentando loggear login activity para cliente:', authData.cliente_id);
          try {
            const { data, error } = await supabase.rpc('log_cliente_activity_simple', {
              p_cliente_id: authData.cliente_id,
              p_action: 'Login exitoso',
              p_entity_type: 'auth',
              p_details: `Cliente ${authData.nombre} inició sesión`,
              p_action_type: 'login'
            });
            
            if (error) {
              console.error('Error en RPC call:', error);
            } else {
              console.log('Login activity logged successfully:', data);
            }
          } catch (logError) {
            console.error('Error logging login activity:', logError);
          }

          return true;
        }
      } else {
        setError(data?.[0]?.message || 'Error de autenticación');
        return false;
      }
    } catch (error) {
      console.error('Error during login:', error);
      setError('Error durante el login');
      return false;
    } finally {
      setIsLoading(false);
    }

    return false;
  };

  // Logout de cliente
  const logout = async () => {
    // Log logout activity before clearing session
    if (cliente) {
      try {
        await supabase.rpc('log_cliente_activity_simple', {
          p_cliente_id: cliente.id,
          p_action: 'Logout',
          p_entity_type: 'auth',
          p_details: `Cliente ${cliente.nombre} cerró sesión`,
          p_action_type: 'logout'
        });
        console.log('Logout activity logged successfully');
      } catch (logError) {
        console.error('Error logging logout activity:', logError);
      }
    }

    localStorage.removeItem('cliente_token');
    setCliente(null);
    setSession(null);
    setError(null);
  };

  const value: ClienteAuthContextType = {
    cliente,
    session,
    isLoading,
    error,
    login,
    logout,
    isAuthenticated: !!cliente && !!session
  };

  return (
    <ClienteAuthContext.Provider value={value}>
      {children}
    </ClienteAuthContext.Provider>
  );
};
